import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { naira, statusBadgeClass, statusLabel } from "../lib/format";
import { Card, Loading } from "../components/UI";
import { Home, AlertTriangle, FileSignature, Pencil, Plus, Archive, RotateCcw } from "lucide-react";
import { useAuth } from "../lib/auth";

export default function Units() {
  const { user } = useAuth();
  const [units, setUnits] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [unitTypes, setUnitTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUnit, setEditingUnit] = useState(null);
  const [addingUnit, setAddingUnit] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [filter, setFilter] = useState("all");

  const reload = async () => {
    const query = showArchived && user?.role === "admin" ? "?include_archived=true" : "";
    const [u, t, ut] = await Promise.all([
      api.get(`/units${query}`),
      api.get("/tenants").catch(() => ({ data: [] })),
      api.get("/unit-types").catch(() => ({ data: [] })),
    ]);
    setUnits(u.data); setTenants(t.data); setUnitTypes(ut.data);
    setLoading(false);
  };
  useEffect(() => { reload(); }, [showArchived]);

  const saveUnit = async (e) => {
    e.preventDefault();
    const form = new FormData(e.target);
    const payload = {
      current_rent_amount: form.get("rent") ? Number(form.get("rent")) : null,
      status: form.get("status"),
      occupant_count: Number(form.get("occupants") || 0),
      notes: form.get("notes") || "",
      tenant_id: form.get("tenant_id") || null,
    };
    try {
      await api.patch(`/units/${editingUnit.id}`, payload);
      toast.success("Unit updated");
      setEditingUnit(null);
      reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const addUnit = async (e) => {
    e.preventDefault();
    const form = new FormData(e.target);
    const payload = {
      unit_name: form.get("unit_name"),
      unit_number: form.get("unit_number"),
      unit_type_name: form.get("unit_type_name"),
      status: "vacant",
      current_rent_amount: form.get("rent") ? Number(form.get("rent")) : null,
      occupant_count: 0,
      notes: form.get("notes") || "",
    };
    try {
      await api.post("/units", payload);
      toast.success("Unit added to inventory");
      setAddingUnit(false);
      reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const toggleActive = async (u) => {
    const willArchive = u.active !== false;
    const verb = willArchive ? "Archive" : "Restore";
    if (!confirm(`${verb} ${u.unit_name}? ${willArchive ? "It will be hidden from managers and the public vacancy page." : "It will reappear in active inventory."}`)) return;
    try {
      await api.patch(`/units/${u.id}/active`, { active: !willArchive });
      toast.success(`Unit ${willArchive ? "archived" : "restored"}`);
      reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  if (loading) return <Loading/>;
  const filtered = filter === "all" ? units : units.filter(u => u.status === filter);
  const isAdmin = user.role === "admin";
  const canEdit = isAdmin;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 fade-up">
        <div>
          <h1 className="font-serif text-3xl font-bold text-[#1A2E24]">Units</h1>
          <p className="text-[#5E7166] text-sm">{units.length} {showArchived ? "total (incl. archived)" : "active"} — {units.filter(u=>u.status==="occupied").length} occupied, {units.filter(u=>u.status==="vacant").length} vacant.</p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          {["all","occupied","vacant","under_maintenance"].map(s => (
            <button key={s} onClick={() => setFilter(s)} className={filter === s ? "btn-emerald text-xs" : "btn-outline-emerald text-xs"} data-testid={`filter-${s}`}>{statusLabel(s)}</button>
          ))}
          {isAdmin && (
            <>
              <label className="text-xs text-[#5E7166] inline-flex items-center gap-1.5 cursor-pointer ml-2" data-testid="show-archived-toggle">
                <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} className="accent-[#0E5C3A]"/>
                Show archived
              </label>
              <button onClick={() => setAddingUnit(true)} className="btn-gold text-xs inline-flex items-center gap-1.5" data-testid="add-unit-btn">
                <Plus size={14}/> Add unit
              </button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map(u => (
          <div key={u.id} className={`card-modav p-5 fade-up ${u.active === false ? "opacity-60 border-dashed" : ""} ${u.notes?.includes('FLAG') || u.notes?.includes('ANOMALY') ? "border-l-4 border-[#C92A2A]" : ""}`} data-testid={`unit-card-${u.unit_number}`}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-[#C9A227]">{u.unit_type_name}</div>
                <h3 className="font-serif text-xl font-bold mt-1">{u.unit_name}</h3>
                <div className="text-xs text-[#5E7166]">#{u.unit_number}</div>
              </div>
              <div className="flex flex-col items-end gap-1">
                <span className={statusBadgeClass(u.status)}>{statusLabel(u.status)}</span>
                {u.active === false && <span className="badge badge-gray text-[10px]">Archived</span>}
              </div>
            </div>
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-[#5E7166]">Rent</span><span className={`font-semibold ${u.current_rent_amount ? "" : "text-[#C92A2A]"}`}>{u.current_rent_amount ? naira(u.current_rent_amount) : "Missing"}</span></div>
              <div className="flex justify-between"><span className="text-[#5E7166]">Tenant</span><span className="font-semibold truncate ml-2 max-w-[60%]">{u.tenant?.full_name || <i className="text-[#5E7166]">Vacant</i>}</span></div>
              <div className="flex justify-between"><span className="text-[#5E7166]">Occupants</span><span>{u.occupant_count || 0}</span></div>
              <div className="flex justify-between"><span className="text-[#5E7166]">Use</span><span className="text-xs">{u.use_description || u.business_or_residential_use}</span></div>
            </div>
            {u.notes && (
              <div className="mt-3 p-2 bg-[#FEF0F0] border border-[#FBC4C4] rounded text-xs text-[#C92A2A] flex gap-1 items-start">
                <AlertTriangle size={12} className="flex-shrink-0 mt-0.5"/> {u.notes}
              </div>
            )}
            {canEdit && (
              <div className="mt-3 flex gap-2 flex-wrap">
                {u.active !== false && (
                  <>
                    <button onClick={() => setEditingUnit(u)} className="btn-outline-emerald text-xs flex-1 justify-center" data-testid={`edit-unit-${u.unit_number}`}><Pencil size={12}/> Edit</button>
                    {u.status === "vacant" && <Link to="/leases" className="btn-gold text-xs flex-1 justify-center"><FileSignature size={12}/> Generate Lease</Link>}
                  </>
                )}
                <button onClick={() => toggleActive(u)} className={`text-xs px-3 py-1.5 rounded-lg border ${u.active === false ? "border-[#0E5C3A] text-[#0E5C3A] hover:bg-[#E8F5EE]" : "border-[#C92A2A] text-[#C92A2A] hover:bg-[#FEF0F0]"} inline-flex items-center gap-1`} data-testid={`toggle-active-${u.unit_number}`}>
                  {u.active === false ? <><RotateCcw size={12}/> Restore</> : <><Archive size={12}/> Archive</>}
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {addingUnit && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="card-modav p-6 w-full max-w-md" data-testid="add-unit-modal">
            <h3 className="font-serif text-xl font-bold mb-4">Add new unit</h3>
            <form onSubmit={addUnit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Unit name *</span>
                  <input name="unit_name" required className="input-modav mt-1" placeholder="Flat 5" data-testid="new-unit-name"/></label>
                <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Unit number *</span>
                  <input name="unit_number" required className="input-modav mt-1" placeholder="F5" data-testid="new-unit-number"/></label>
              </div>
              <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Unit type *</span>
                <select name="unit_type_name" required className="input-modav mt-1" data-testid="new-unit-type">
                  <option value="">— Select —</option>
                  {unitTypes.map(ut => <option key={ut.id} value={ut.name}>{ut.name}</option>)}
                </select></label>
              <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Rent (₦)</span>
                <input name="rent" type="number" className="input-modav mt-1" placeholder="leave blank to use pricing default"/></label>
              <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Notes</span>
                <textarea name="notes" rows={2} className="input-modav mt-1"/></label>
              <div className="flex gap-2 pt-2">
                <button type="button" onClick={() => setAddingUnit(false)} className="btn-ghost flex-1 justify-center">Cancel</button>
                <button type="submit" className="btn-gold flex-1 justify-center" data-testid="save-new-unit-btn">Add unit</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editingUnit && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="card-modav p-6 w-full max-w-md" data-testid="edit-unit-modal">
            <h3 className="font-serif text-xl font-bold mb-4">Edit {editingUnit.unit_name}</h3>
            <form onSubmit={saveUnit} className="space-y-3">
              <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Rent (₦)</span>
                <input name="rent" type="number" defaultValue={editingUnit.current_rent_amount || ""} className="input-modav mt-1" data-testid="edit-rent"/></label>
              <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Status</span>
                <select name="status" defaultValue={editingUnit.status} className="input-modav mt-1" data-testid="edit-status">
                  <option value="occupied">Occupied</option>
                  <option value="vacant">Vacant</option>
                  <option value="under_maintenance">Under Maintenance</option>
                </select></label>
              <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Tenant</span>
                <select name="tenant_id" defaultValue={editingUnit.tenant_id || ""} className="input-modav mt-1">
                  <option value="">— None —</option>
                  {tenants.map(t => <option key={t.id} value={t.id}>{t.full_name}</option>)}
                </select></label>
              <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Occupants</span>
                <input name="occupants" type="number" defaultValue={editingUnit.occupant_count || 0} className="input-modav mt-1"/></label>
              <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Notes</span>
                <textarea name="notes" defaultValue={editingUnit.notes || ""} className="input-modav mt-1" rows={3}/></label>
              <div className="flex gap-2 pt-2">
                <button type="button" onClick={() => setEditingUnit(null)} className="btn-ghost flex-1 justify-center">Cancel</button>
                <button type="submit" className="btn-gold flex-1 justify-center" data-testid="save-unit-btn">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
